"use client";

import { useId, useState } from "react";
import classNames from "@/helpers/classNames";
import Text from "@/components/Text";
import styles from "./styles.module.scss";

export type TooltipPosition = "top" | "bottom" | "left" | "right";
export type TooltipAlign = "start" | "center" | "end";

export interface TooltipProps {
  text: string;
  /** Side of the trigger the tooltip appears on. Defaults to "bottom". */
  position?: TooltipPosition;
  /**
   * Cross-axis alignment relative to the trigger. With `top`/`bottom` the
   * tooltip biases horizontally; with `left`/`right` it biases vertically.
   * Defaults to "center".
   */
  align?: TooltipAlign;
  children: React.ReactNode;
  className?: string;
  label?: string;
}

const Tooltip = ({
  text,
  position = "bottom",
  align = "center",
  children,
  className,
  label = "More information",
}: TooltipProps) => {
  const id = useId();
  // WCAG 1.4.13 requires the tooltip be dismissible without moving the pointer
  // or focus. Escape hides it until the trigger is left and re-entered.
  const [dismissed, setDismissed] = useState(false);

  return (
    <span className={classNames(styles.trigger, className, { [styles.dismissed]: dismissed })}>
      {/* Handlers sit on the button rather than the wrapper: it is the element
          that takes focus and hover, and putting them on a bare <span> would
          be an interaction handler on a non-interactive element. */}
      <button
        type="button"
        className={styles.button}
        aria-label={label}
        aria-describedby={id}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setDismissed(true);
          }
        }}
        onMouseEnter={() => setDismissed(false)}
        onMouseLeave={() => setDismissed(false)}
        onBlur={() => setDismissed(false)}
      >
        {children}
      </button>
      <span
        id={id}
        role="tooltip"
        className={classNames(
          styles.content,
          {
            [styles.positionTop]: position === "top",
            [styles.positionRight]: position === "right",
            [styles.positionLeft]: position === "left",
          },
          {
            [styles.alignStart]: align === "start",
            [styles.alignEnd]: align === "end",
          },
        )}
      >
        <Text as="span" text={text} variant="label" size="sm" className={styles.text} />
      </span>
    </span>
  );
};

export default Tooltip;
